/**
 * Typed card-save failure — the classification, carried across a throw.
 *
 * `useCardSave` and its three call sites (booking, subscription, account setup)
 * are built on a throw contract, so converting the card-save hooks to return the
 * envelope wholesale would be a behavioural edit across three pages. This is the
 * escape hatch
 * [internalapi-envelope-rollout § "Conversions that are not mechanical"](/docs/refactors/internalapi-envelope-rollout.md)
 * sanctions instead — the same standing granted to `WrongClubError` and
 * `AccessLinkError`: keep the throw, but carry `message` and `error.code` on it so
 * nothing downstream has to substring-match to know what happened.
 *
 * The `message` is the API's own copy — for a refused card that is the reason we
 * already computed server-side ("We couldn't verify that card…"), which is what
 * a member should read. Before the move off `legacyApiClient` the body was
 * discarded and this said `"Server error: 500"`.
 *
 * @module features/payment/errors
 */

import type { ApiErrorResult } from '~/lib/apiClient';

/** Fallback when the API gave us nothing readable (offline, timeout, empty body). */
const FALLBACK_MESSAGE = 'We could not save your card. Please try again.';

export class PaymentSaveError extends Error {
  /**
   * Machine-readable cause from the envelope, when the API sent one.
   *
   * Card-save codes worth branching on: `card_declined` (the issuer said no),
   * `invalid_card_data` (details unusable — ask for a re-entry),
   * `card_token_stale` (the form's token was spent; re-mount the card form).
   */
  readonly code?: string;

  /** HTTP status, for the rare caller that needs it (e.g. telling 402 from 409). */
  readonly status?: number;

  // Takes the FAILURE arm, not the `ApiResult` union: `error` exists only on that
  // arm, so the union does not compile here and optional chaining cannot rescue a
  // property absent from a member. Both call sites already narrow with
  // `if (!result.success)` before constructing.
  constructor(result: ApiErrorResult) {
    super(result.message || FALLBACK_MESSAGE);
    this.name = 'PaymentSaveError';
    this.code = result.error?.code;
    this.status = result.httpStatus;

    // Keep `instanceof` working across the transpiled class.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** True when a thrown value is a classified card-save failure. */
export function isPaymentSaveError(err: unknown): err is PaymentSaveError {
  return err instanceof PaymentSaveError;
}
